import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../data/content";

const LINKS = [
  { href: "#services", label: "Services" },
  { href: "#interiors", label: "Interiors & Costs" },
  { href: "#projects", label: "Projects" },
  { href: "#videos", label: "Videos" },
  { href: "#founder", label: "Founder" },
  { href: "#contact", label: "Contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView();
  }
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,box-shadow] duration-300 backdrop-blur-xl ${
        scrolled ? "bg-white/85 border-navy/10 shadow-sm" : "bg-white/60 border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3"
        >
          <img
            src="/logo-mark.png"
            alt="Subhankara Design & Construction logo"
            className="h-10 w-10 border border-navy/15 bg-white object-contain"
          />
          <span className="leading-tight">
            <span className="block font-display text-sm font-extrabold tracking-tight text-navy">
              SUBHANKARA
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Design & Construction
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={(e) => go(e, l.href)}
              className="group relative font-mono text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 transition-colors hover:text-navy"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-electric transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="nav-phone-button"
            className="flex items-center gap-2 border border-navy px-4 py-2 font-mono text-xs font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
          >
            <Phone size={13} /> {COMPANY.phone}
          </a>
          <button
            data-testid="nav-enquire-button"
            onClick={(e) => go(e, "#contact")}
            className="bg-electric px-4 py-2 font-mono text-xs font-semibold text-white transition-colors hover:bg-navy"
          >
            ENQUIRE
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-navy/10 bg-white/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col px-5 py-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="border-b border-slate-100 py-3 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-navy"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="nav-mobile-phone"
              className="py-3 font-mono text-sm font-semibold text-electric"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
